/**
 * KinsCircle Phase 6D.2 — Memory Playlist Card.
 *
 * STABILITY.md §13–§14:
 *   - READ-ONLY surface that lets families browse curated playlists of
 *     memories without ever editing or persisting the underlying memories.
 *   - Playlists reference memory IDs only, never duplicate captions or
 *     storage URLs into a separate row.
 *   - NO autoplay. NO auto-save. The "Continue where you left off" hint
 *     reads from local-only `legacyBookmarks` storage.
 *   - Renders null when `memoryPlaylist` flag is OFF or no qualifying
 *     collections exist.
 *   - Mandatory citation footer on every playlist tile.
 */

import React, { useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  ListMusic,
  PlayCircle,
  Users,
  Calendar,
  Bookmark,
  BookmarkCheck,
} from 'lucide-react';
import {
  isMemoryPlaylistEnabled,
  isStoryPlaybackEnabled,
  isLegacyBookmarksEnabled,
} from '@/lib/ai/featureFlag';
import {
  buildThemeCollection,
  buildPersonJourneyCollection,
  buildMilestoneCollection,
  rankCollectionMoments,
  collectionToPlaybackSlides,
  type LegacyCollection,
} from '@/lib/ai/legacyCollectionEngine';
import {
  isBookmarked,
  saveLegacyBookmark,
  removeLegacyBookmark,
} from '@/lib/ai/legacyBookmarks';
import StoryPlaybackModal, { type PlaybackStory } from './StoryPlaybackModal';

const TONES = [
  'from-amber-100 via-pink-100 to-fuchsia-200',
  'from-rose-100 via-fuchsia-100 to-purple-200',
  'from-sky-100 via-cyan-100 to-emerald-100',
  'from-orange-100 via-amber-100 to-yellow-100',
  'from-indigo-100 via-violet-100 to-fuchsia-200',
  'from-emerald-100 via-teal-100 to-sky-100',
];

const formatYearRange = (r: { from: number | null; to: number | null }): string => {
  if (r.from && r.to) return r.from === r.to ? `${r.from}` : `${r.from}–${r.to}`;
  if (r.from) return `${r.from}`;
  if (r.to) return `${r.to}`;
  return '';
};

const MemoryPlaylistCard: React.FC = () => {
  if (!isMemoryPlaylistEnabled()) return null;

  const playlists = useMemo<LegacyCollection[]>(() => {
    // Combine theme + person + milestone collections, then rank by impact.
    const all = [
      ...buildThemeCollection(4),
      ...buildPersonJourneyCollection(4),
      ...buildMilestoneCollection(4),
    ];
    if (!all.length) return [];
    return rankCollectionMoments(all)
      .map((r) => r.collection)
      .slice(0, 9);
  }, []);

  const [active, setActive] = useState<LegacyCollection | null>(null);
  const [playOpen, setPlayOpen] = useState(false);
  // Force re-render when bookmarks toggle so icon state stays in sync.
  const [bookmarkBump, setBookmarkBump] = useState(0);

  const story = useMemo<PlaybackStory | null>(() => {
    if (!active) return null;
    if (!isStoryPlaybackEnabled()) return null;
    const slides = collectionToPlaybackSlides(active, 14);
    if (!slides.length) return null;
    return {
      label: active.title,
      subtitle: active.citation,
      slides,
    };
  }, [active]);

  if (!playlists.length) return null;

  const toggleBookmark = (p: LegacyCollection) => {
    if (!isLegacyBookmarksEnabled()) return;
    const already = isBookmarked('playlist', p.id);
    if (already) removeLegacyBookmark('playlist', p.id);
    else saveLegacyBookmark({ kind: 'playlist', targetId: p.id, label: p.title });
    setBookmarkBump((n) => n + 1);
  };

  return (
    <section className="py-12 px-4 bg-gradient-to-b from-white to-fuchsia-50/40">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-2 mb-4">
          <ListMusic className="w-5 h-5 text-fuchsia-600" />
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-slate-900">
            Memory Playlists
          </h2>
          <span className="text-[10px] uppercase tracking-wide font-semibold text-fuchsia-700 bg-fuchsia-100 px-2 py-0.5 rounded-full">
            Beta
          </span>
        </div>
        <p className="text-sm text-slate-600 max-w-2xl mb-6">
          Curated playlists of moments your family has already captured —
          grouped by theme, person, and milestone. Tap any playlist to
          play it as a cinematic story.
        </p>

        <div
          key={bookmarkBump}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
        >
          {playlists.map((p, idx) => {
            const tone = TONES[idx % TONES.length];
            const range = formatYearRange(p.yearRange);
            const saved = isBookmarked('playlist', p.id);
            return (
              <Card
                key={p.id}
                className="overflow-hidden border border-slate-100 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className={`bg-gradient-to-br ${tone} p-5 min-h-[140px] flex flex-col justify-between`}>
                  <div className="flex items-start justify-between gap-2">
                    <Badge className="bg-white/80 text-slate-800 border border-white/60 hover:bg-white/80 text-[10px] uppercase tracking-wide">
                      {p.kind}
                    </Badge>
                    {isLegacyBookmarksEnabled() && (
                      <button
                        onClick={() => toggleBookmark(p)}
                        aria-label={saved ? 'Remove bookmark' : 'Save bookmark'}
                        className="p-1.5 rounded-full bg-white/70 hover:bg-white text-slate-700"
                      >
                        {saved ? (
                          <BookmarkCheck className="w-4 h-4" />
                        ) : (
                          <Bookmark className="w-4 h-4" />
                        )}
                      </button>
                    )}
                  </div>
                  <div>
                    <h3 className="font-serif text-lg md:text-xl font-bold text-slate-900 leading-tight mt-3">
                      {p.title}
                    </h3>
                    {range && (
                      <div className="mt-1.5 inline-flex items-center gap-1 text-xs text-slate-700/80">
                        <Calendar className="w-3 h-3" />
                        {range}
                      </div>
                    )}
                  </div>
                </div>
                <CardContent className="p-4 space-y-3 bg-white">
                  {p.topPeople.length > 0 && (
                    <div className="flex items-center gap-1.5 text-xs text-slate-600 truncate">
                      <Users className="w-3.5 h-3.5 shrink-0" />
                      <span className="truncate">
                        {p.topPeople.slice(0, 4).map((n) =>
                          n
                            .split(' ')
                            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                            .join(' '),
                        ).join(', ')}
                        {p.topPeople.length > 4 && ` +${p.topPeople.length - 4} more`}
                      </span>
                    </div>
                  )}
                  {p.topThemes.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {p.topThemes.slice(0, 3).map((t) => (
                        <Badge
                          key={t}
                          variant="outline"
                          className="text-[10px] capitalize"
                        >
                          {t}
                        </Badge>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center justify-between pt-1">
                    <div className="text-[11px] italic text-slate-500">
                      {p.memoryCount} memor{p.memoryCount === 1 ? 'y' : 'ies'}
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setActive(p);
                        if (isStoryPlaybackEnabled()) setPlayOpen(true);
                      }}
                      className="text-fuchsia-700 hover:bg-fuchsia-50 h-8 px-2"
                      disabled={!isStoryPlaybackEnabled()}
                      title={
                        isStoryPlaybackEnabled()
                          ? 'Play this playlist'
                          : 'Story playback is disabled'
                      }
                    >
                      <PlayCircle className="w-4 h-4 mr-1" />
                      Play
                    </Button>
                  </div>
                  <div className="text-[10px] italic text-slate-400 pt-1 border-t border-slate-100">
                    {p.citation}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <p className="text-[11px] text-slate-400 mt-6 text-center">
          Playlists never edit your original memories. They reference them in
          place — read-only, anchored to the moments you already saved.
        </p>
      </div>

      <StoryPlaybackModal
        open={playOpen}
        onOpenChange={setPlayOpen}
        story={story}
      />
    </section>
  );
};

export default MemoryPlaylistCard;
